import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ComicCard from "@/components/base/ComicCard";
import SectionHeading from "@/components/base/SectionHeading";
import { comics, categories, rankings, testimonials, comicById } from "@/mocks/comics";

const featureHighlights = [
  {
    icon: "ri-bookmark-3-fill",
    title: "书架收藏",
    desc: "一键收藏心水漫画，书架分类整理，追更清单一目了然。",
    color: "bg-primary-100 text-primary-800",
  },
  {
    icon: "ri-notification-3-fill",
    title: "更新订阅",
    desc: "订阅连载作者与作品，更新第一时间提醒，不错过任何一话。",
    color: "bg-accent-100 text-accent-800",
  },
  {
    icon: "ri-download-cloud-2-fill",
    title: "离线缓存",
    desc: "没有网络也能看，缓存的漫画想存多少存多少。",
    color: "bg-secondary-100 text-secondary-800",
  },
  {
    icon: "ri-history-fill",
    title: "历史记录",
    desc: "阅读进度自动同步，换设备也能无缝接着看。",
    color: "bg-primary-100 text-primary-800",
  },
];

const faqPreview = [
  {
    q: "怎么把喜欢的漫画收藏到书架？",
    a: "在漫画详情页点击「加入书架」即可一键收藏，还可以在书架中按分类、作者自由整理。",
  },
  {
    q: "订阅更新后能收到提醒吗？",
    a: "会。订阅的漫画一旦更新，首页和个人中心都会第一时间显示，让你追更不遗漏。",
  },
  {
    q: "离线缓存会占用很多空间吗？",
    a: "支持按章节或整部缓存，也可手动清理，缓存管理非常灵活，完全由你掌控。",
  },
  {
    q: "换了设备，阅读历史还在吗？",
    a: "在的。历史记录与账号绑定，登录后在任意设备上都能看到并续读。",
  },
];

export default function Home() {
  const [cat, setCat] = useState("全部");
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(
    () => (cat === "全部" ? comics : comics.filter((c) => c.category === cat)),
    [cat],
  );

  const top5 = rankings.slice(0, 5);

  const doSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) navigate(`/ranking?q=${encodeURIComponent(keyword.trim())}`);
  };

  return (
    <div>
      {/* ===== 首屏 Hero ===== */}
      <section className="relative flex min-h-[560px] w-full items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20illustration%20of%20a%20cozy%20reading%20collection%20of%20stacked%20manga%20books%20and%20open%20pages%20floating%20with%20comic%20panels%2C%20warm%20coral%20orange%20and%20deep%20teal%20palette%2C%20artistic%20flat%20vector%20style%2C%20soft%20glow%2C%20cinematic%20composition%2C%20clean%20and%20inviting&width=1600&height=900&seq=hero-home&orientation=landscape"
          alt="新新漫画网海量漫画阅读背景图"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/40 to-foreground-950/80"></div>

        <div className="relative z-10 mx-auto flex w-full max-w-4xl flex-col items-center px-4 py-16 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-200/40 bg-background-50/10 px-4 py-1.5 text-xs font-medium text-background-100 backdrop-blur">
            <i className="ri-sparkling-2-line text-primary-300"></i>
            收藏 · 订阅 · 离线 · 历史记录 · 个性化推荐
          </span>

          <h1 className="mt-6 font-heading text-4xl leading-tight text-background-50 md:text-5xl">
            海量漫画，随心追更
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            新新漫画网为每一位漫画爱好者打造个性化阅读体验。收藏、订阅、离线、历史记录与推荐全支持，追更更顺手，看书更自在。
          </p>

          <form onSubmit={doSearch} className="mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索你想看的漫画，比如「破晓之刃」"
                className="h-12 w-full rounded-full border border-background-200/40 bg-background-50/95 pl-12 pr-4 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-400"
              />
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"></i>
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              立即搜索
            </button>
          </form>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
            <Link
              to="/ranking"
              className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-4 py-2 text-sm text-background-100 backdrop-blur transition hover:bg-background-50/20"
            >
              <i className="ri-fire-fill text-secondary-400"></i> 今日热门
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-4 py-2 text-sm text-background-100 backdrop-blur transition hover:bg-background-50/20"
            >
              <i className="ri-settings-4-line text-primary-300"></i> 阅读功能
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-4 py-2 text-sm text-background-100 backdrop-blur transition hover:bg-background-50/20"
            >
              <i className="ri-question-line text-accent-300"></i> 使用帮助
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 热门漫画 + 分类筛选 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="编辑精选"
            title="正在热播的热门漫画"
            desc="精选人气连载与口碑完结佳作，总有一部合你胃口。"
          />
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition ${
                  cat === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-6">
          {filtered.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* ===== 阅读功能导览 ===== */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            center
            eyebrow="个性化阅读体验"
            title="五大功能，让追更更顺手"
            desc="从发现到阅读，每一个环节都为你的体验用心设计。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featureHighlights.map((f) => (
              <Link
                to="/features"
                key={f.title}
                className="group rounded-lg border border-background-200/70 bg-background-50 p-5 transition hover:-translate-y-1 hover:border-accent-300"
              >
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-lg ${f.color}`}
                >
                  <i className={`${f.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                  了解更多
                  <i className="ri-arrow-right-line transition group-hover:translate-x-1"></i>
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-6 rounded-lg bg-accent-500 px-6 py-4 text-center text-background-50">
            <p className="text-sm font-medium">
              还有「历史记录自动同步」与「个性化推荐」等你解锁
            </p>
            <Link
              to="/features"
              className="mt-2 inline-block rounded-full bg-background-50 px-5 py-2 text-sm font-medium text-accent-900 transition hover:bg-background-100"
            >
              查看完整功能说明 →
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 追更排行 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <SectionHeading
            eyebrow="实时数据"
            title="本周追更排行榜"
            desc="真实人气数据，看看大家都在追什么。"
          />
          <Link
            to="/ranking"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            查看完整榜单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
          {top5.map((r) => {
            const comic = comicById(r.comicId);
            if (!comic) return null;
            return (
              <article
                key={r.rank}
                className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-3 transition hover:border-secondary-300"
              >
                <span className="font-heading text-3xl text-secondary-400">
                  {r.rank}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-foreground-950">{comic.name}</h3>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {comic.category} · 周人气 {r.weekly}
                  </p>
                </div>
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-sm ${
                    r.trend === "up"
                      ? "bg-primary-100 text-primary-700"
                      : r.trend === "down"
                        ? "bg-background-100 text-foreground-400"
                        : "bg-secondary-100 text-secondary-700"
                  }`}
                >
                  <i
                    className={`${
                      r.trend === "up"
                        ? "ri-arrow-up-line"
                        : r.trend === "down"
                          ? "ri-arrow-down-line"
                          : "ri-subtract-line"
                    }`}
                  ></i>
                </span>
              </article>
            );
          })}
        </div>
      </section>

      {/* ===== 读者口碑 ===== */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading
            center
            eyebrow="读者口碑"
            title="他们说，新新漫画网很好用"
            desc="来自真实读者的体验分享，也是我们持续改进的动力。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <div className="flex gap-0.5 text-secondary-500">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={`${t.name}头像`}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-bold text-foreground-950">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ 预览 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="使用帮助"
              title="常见问题速览"
              desc="关于收藏、订阅、离线、历史记录等功能的常见疑问，这里都有答案。"
            />
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              查看全部常见问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="flex flex-col gap-3">
            {faqPreview.map((item) => (
              <details
                key={item.q}
                className="group rounded-lg border border-background-200/70 bg-background-50 p-4"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                  {item.q}
                  <i className="ri-add-line text-secondary-600 transition group-open:rotate-45"></i>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="relative flex flex-col items-center overflow-hidden rounded-xl px-6 py-14 text-center">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20stylized%20illustration%20of%20an%20open%20comic%20book%20with%20colorful%20panels%20sparkling%20into%20the%20air%2C%20warm%20coral%20and%20teal%20palette%2C%20playful%20creative%20vector%20style%2C%20inviting%20and%20lively%2C%20clean%20background&width=1600&height=600&seq=cta-banner&orientation=landscape"
            alt="开启个性化阅读背景图"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/55"></div>
          <div className="relative z-10 flex w-full flex-col items-center">
            <h2 className="font-heading text-2xl text-background-50 md:text-3xl">
              现在开始你的追更之旅
            </h2>
            <p className="mt-3 max-w-xl text-sm text-background-200">
              收藏、订阅、离线、历史记录与个性化推荐，一站式搞定。登录后更可跨设备同步阅读进度。
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/ranking"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                去排行榜逛逛
              </Link>
              <Link
                to="/features"
                className="whitespace-nowrap rounded-md bg-background-50/15 px-6 py-3 text-sm font-medium text-background-50 backdrop-blur transition hover:bg-background-50/25"
              >
                了解阅读功能
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}